import { useEffect, useState } from "react";
import { theme, type as text } from "../theme";
import { api } from "../api/client";
import { StatusTag, type StatusTone } from "./Drawing";
import { PortalLink } from "./PortalLink";

// Record checks beside the AI verdict on a control (backend lib/recordChecks.ts): deterministic
// tests of the organisation's own registers, each with a plain reason and the records it read.
// A failed check caps the AI verdict at "partly met"; a passed one supports it but never decides
// it. This block only shows what the server computed — it never scores anything itself.

export type RecordCheckStatus = "pass" | "fail" | "not_applicable" | "no_data";

export interface RecordCheckRecord {
  kind: string;
  id: string;
  label: string;
  href: string;
}

export interface RecordCheckResult {
  checkId: string;
  title: string;
  status: RecordCheckStatus;
  reason: string;
  records: RecordCheckRecord[];
  recordCount: number;
  scope: string;
}

const STATUS: Record<RecordCheckStatus, { tone: StatusTone; label: string; title: string }> = {
  pass:           { tone: "met",  label: "Pass",           title: "The records meet this test. Supporting evidence only — it never makes the control met on its own." },
  fail:           { tone: "gap",  label: "Fail",           title: "The records show the requirement unmet. The AI verdict on this control is capped at partly met." },
  no_data:        { tone: "none", label: "No data",        title: "Nothing in the register to judge yet. Not a pass." },
  not_applicable: { tone: "none", label: "Not applicable", title: "The records show nothing this test reaches. Not a pass." },
};

// Failures first, then what passed, then what could not be judged.
const ORDER: Record<RecordCheckStatus, number> = { fail: 0, pass: 1, no_data: 2, not_applicable: 3 };

/** Only internal paths are linked; anything else is shown as text. */
const isInternalPath = (href: string) => href.startsWith("/") && !href.startsWith("//");

export function RecordChecks({ results }: { results: RecordCheckResult[] }) {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  if (results.length === 0) return null;
  const sorted = [...results].sort((a, b) => ORDER[a.status] - ORDER[b.status] || a.title.localeCompare(b.title));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={text.hint}>
        Tests of your own registers. A failed check caps the AI verdict at partly met; a passed check supports it but never makes a control met on its own.
      </div>
      {sorted.map((r) => {
        const s = STATUS[r.status];
        const expanded = !!open[r.checkId];
        const more = r.recordCount - r.records.length;
        return (
          <div key={r.checkId} style={{ border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "8px 10px", background: "var(--c-panel)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <StatusTag tone={s.tone} title={s.title}>{s.label}</StatusTag>
              <span style={{ ...text.body, fontWeight: 600 }}>{r.title}</span>
            </div>
            <div style={{ ...text.hint, marginTop: 4 }}>{r.reason}</div>
            {r.records.length > 0 && (
              <>
                <button
                  type="button"
                  onClick={() => setOpen((o) => ({ ...o, [r.checkId]: !expanded }))}
                  aria-expanded={expanded}
                  style={{ ...text.hint, marginTop: 6, padding: 0, border: "none", background: "transparent", color: theme.color.ref, cursor: "pointer" }}
                >
                  {expanded ? "Hide the records" : `Show the records (${r.recordCount})`}
                </button>
                {expanded && (
                  <ul style={{ margin: "6px 0 0", paddingLeft: 18, display: "flex", flexDirection: "column", gap: 3 }}>
                    {r.records.map((rec) => (
                      <li key={`${rec.kind}:${rec.id}`} style={text.hint}>
                        {isInternalPath(rec.href)
                          ? <PortalLink to={rec.href} style={{ color: theme.color.ref }}>{rec.label}</PortalLink>
                          : rec.label}
                      </li>
                    ))}
                    {more > 0 && <li style={{ ...text.hint, listStyle: "none" }}>and {more} more</li>}
                  </ul>
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Record checks for pages whose rows do not come from /controls/soa (legal obligations, Cyber
 * Essentials keys): one request, results by control id.
 */
export function useRecordChecks(enabled = true): Record<string, RecordCheckResult[]> {
  const [byControl, setByControl] = useState<Record<string, RecordCheckResult[]>>({});
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    api.get<{ byControl: Record<string, RecordCheckResult[]> }>("/controls/record-checks")
      .then((d) => { if (live) setByControl(d.byControl ?? {}); })
      .catch(() => { /* the control detail shows without them */ });
    return () => { live = false; };
  }, [enabled]);
  return byControl;
}
